import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { createPortal } from 'react-dom'
import { AlertTriangle, CheckCircle2, Info, X, XCircle } from 'lucide-react'
import { Button } from './Button'

export type ToastTone = 'success' | 'error' | 'warning' | 'info'

export interface ToastInput {
  title: string
  description?: string
  tone?: ToastTone
  /** Durée en ms ; 0 = reste affiché jusqu'à fermeture. */
  duration?: number
}

interface ToastItem extends Required<Omit<ToastInput, 'description'>> {
  id: number
  description?: string
}

interface ToastApi {
  show: (toast: ToastInput) => number
  success: (title: string, description?: string) => number
  error: (title: string, description?: string) => number
  dismiss: (id: number) => void
}

const ToastContext = createContext<ToastApi | null>(null)

const ICONS = { success: CheckCircle2, error: XCircle, warning: AlertTriangle, info: Info }

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([])
  const nextId = useRef(1)

  const dismiss = useCallback((id: number) => {
    setItems((current) => current.filter((item) => item.id !== id))
  }, [])

  const show = useCallback(
    ({ title, description, tone = 'info', duration = 5000 }: ToastInput) => {
      const id = nextId.current++
      setItems((current) => [...current.slice(-4), { id, title, description, tone, duration }])
      if (duration > 0) setTimeout(() => dismiss(id), duration)
      return id
    },
    [dismiss]
  )

  const api = useMemo<ToastApi>(
    () => ({
      show,
      dismiss,
      success: (title, description) => show({ title, description, tone: 'success' }),
      // Les erreurs restent plus longtemps : l'utilisateur doit pouvoir les lire.
      error: (title, description) => show({ title, description, tone: 'error', duration: 10000 }),
    }),
    [show, dismiss]
  )

  return (
    <ToastContext.Provider value={api}>
      {children}
      {createPortal(
        <div className="toast-region" role="region" aria-label="Notifications" aria-live="polite">
          {items.map((item) => {
            const Icon = ICONS[item.tone]
            return (
              <div
                key={item.id}
                className={`toast toast-${item.tone}`}
                role={item.tone === 'error' ? 'alert' : 'status'}
              >
                <Icon aria-hidden="true" width={18} height={18} />
                <div className="toast-body">
                  <div className="toast-title">{item.title}</div>
                  {item.description && <div className="text-muted">{item.description}</div>}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  iconOnly
                  icon={<X />}
                  aria-label="Fermer"
                  onClick={() => dismiss(item.id)}
                />
              </div>
            )
          })}
        </div>,
        document.body
      )}
    </ToastContext.Provider>
  )
}

export function useToast(): ToastApi {
  const context = useContext(ToastContext)
  if (!context) throw new Error('useToast doit être utilisé dans <ToastProvider>.')
  return context
}
